import { useQuery } from '@tanstack/react-query';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { fulfillmentContextService } from '~/services';
import type { PartnerPostalCodeMappingCreate } from '@shipium-corp/shipium-ts-types';
import { DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY } from './dockLaneQueryKeys';

export { DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY };

export interface PostalCodeMappingsParams {
  originId: string;
  laneSortConfigurationId: string;
  postalCodeMappingIds: Record<string, string>;
}

export const dockLanePostalCodeMappingsQuery = {
  queryKey: DOCK_LANE_POSTAL_CODE_MAPPINGS_QUERY_KEY,
  configurationType: 'cached' as const,
  fn: async (params: PostalCodeMappingsParams): Promise<PartnerPostalCodeMappingCreate[]> => {
    const responses = await Promise.all(
      Object.values(params.postalCodeMappingIds).map((mappingId) =>
        fulfillmentContextService.fetchDockLanePostalMapping({
          originId: params.originId,
          laneSortConfigurationId: params.laneSortConfigurationId,
          postalCodeMappingId: mappingId,
        }),
      ),
    );
    const allMappings: PartnerPostalCodeMappingCreate[] = [];
    for (const response of responses) {
      if (response?.postalCodeMappingCreate) {
        allMappings.push(...response.postalCodeMappingCreate);
      }
    }
    return allMappings;
  },
};

interface UseDockLanePostalCodeMappingsOptions {
  originId: string | undefined;
  laneSortConfigurationId: string | undefined;
  postalCodeMappingIds: Record<string, string> | undefined;
  laneId?: string;
}

export const useDockLanePostalCodeMappings = ({
  originId,
  laneSortConfigurationId,
  postalCodeMappingIds,
  laneId,
}: UseDockLanePostalCodeMappingsOptions) => {
  return useQuery({
    ...useQueryCreator(dockLanePostalCodeMappingsQuery, {
      args: [
        {
          originId: originId!,
          laneSortConfigurationId: laneSortConfigurationId!,
          postalCodeMappingIds: postalCodeMappingIds!,
        },
      ],
    }),
    enabled: !!originId && !!laneSortConfigurationId && !!postalCodeMappingIds,
    select: laneId
      ? (data: PartnerPostalCodeMappingCreate[]) => data.filter((entry) => entry.laneId === laneId)
      : undefined,
  });
};
